import AddIcon from '@mui/icons-material/Add';
import { Box, Button, Card, Skeleton, Typography } from '@mui/material';
import { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';

import {
  buildDefinitionFromStore,
  selectCriteria,
  selectHasUnresolvedStubs,
  selectIsEditable,
  selectIsSaving,
  selectLatestStatus,
  useCohortDefinitionStore,
} from '@verantos-dev/app-state-cohort-definition';
import { useIsWaitingForResponse } from '@verantos-dev/app-state-research-assistant';
import { CohortDefinitionStatus } from '@verantos-dev/types-cohorts';

import { CriterionCard } from './CriterionCard/CriterionCard';
import { useCriteriaSection } from './useCriteriaSection';
import { AddVariableFromLibraryModal } from '../../../components/AddVariableFromLibraryModal/AddVariableFromLibraryModal';
import { SortableCriteriaItem } from '../../../components/CriteriaViewCard/SortableCriteriaItem';
import { SortableCriteriaList } from '../../../components/CriteriaViewCard/SortableCriteriaList';
import { SetIndexDateModal } from '../../../components/SetIndexDateModal/SetIndexDateModal';
import { useEmitResearchAssistantUpdate } from '../../../hooks/common/useEmitResearchAssistantUpdate';

interface CriteriaSectionProps {
  isLoading?: boolean;
}

/**
 * Criteria section for the gen-3 cohort detail Definition tab. Pure
 * Zustand; AV-only — surfaces analytical-variable and index-date
 * criteria.
 *
 * Editing affordances mirror the legacy V2 container: Set index date
 * and Add from library are both disabled while the cohort is
 * processing / final / saving.
 *
 * Layout: criterion cards live in a fluid CSS grid that drops columns
 * as the panel narrows (1 col under ~340px, 2 cols up to ~640px,
 * etc.). Same `minmax(280px, 1fr)` formula the artifact list uses, so
 * the Results / artifact / criteria surfaces all reflow consistently.
 *
 * Loading state: while `isLoading` is true, render two skeleton cards
 * in the same grid so the layout doesn't jump when real data arrives.
 */
export const CriteriaSection = ({ isLoading = false }: CriteriaSectionProps) => {
  const intl = useIntl();
  const rows = useCriteriaSection();
  const criteria = useCohortDefinitionStore(selectCriteria);
  const isEditable = useCohortDefinitionStore(selectIsEditable);
  const latestStatus = useCohortDefinitionStore(selectLatestStatus);
  const isSaving = useCohortDefinitionStore(selectIsSaving);
  const hasUnresolvedStubs = useCohortDefinitionStore(selectHasUnresolvedStubs);
  const isWaitingForRA = useIsWaitingForResponse();
  const reorderCriteria = useCohortDefinitionStore((s) => s.reorderCriteria);
  const emitResearchAssistantUpdate = useEmitResearchAssistantUpdate();

  // Persist a completed drag the same way every other criteria edit does
  // (see `useCriterionCard.ts`'s `handleDelete`, OR-6110): the Zustand action
  // above mutates the store synchronously, so `buildDefinitionFromStore`
  // already reflects the new order. Without this call the reorder would only
  // ever live in memory and revert on refresh — autosave isn't triggered by
  // a generic store watcher, each edit site opts in explicitly.
  const handleReorderCriteria = useCallback(
    (activeKey: string, overKey: string) => {
      reorderCriteria(activeKey, overKey);
      emitResearchAssistantUpdate(buildDefinitionFromStore());
    },
    [reorderCriteria, emitResearchAssistantUpdate]
  );

  const [isAddFromLibraryOpen, setIsAddFromLibraryOpen] = useState(false);
  const [isSetIndexDateOpen, setIsSetIndexDateOpen] = useState(false);

  const hasIndexDate = criteria.some((c) => c.criteriaType === 'index_date');
  const isProcessing = latestStatus === CohortDefinitionStatus.PROCESSING;
  const isFinal = latestStatus === CohortDefinitionStatus.FINAL;
  // Single-writer lock (OR-6078): add / set-index are off-limits while the
  // agent owns the cohort — a turn/checkpoint in flight (`isWaitingForRA`) or
  // unresolved stubs mid-assembly (`hasUnresolvedStubs`) — plus the existing
  // processing / final / saving guards.
  const actionsDisabled =
    isProcessing || isFinal || isSaving || isWaitingForRA || hasUnresolvedStubs;

  const setIndexDateDisabled = !isEditable || hasIndexDate || actionsDisabled;
  const addFromLibraryDisabled = !isEditable || actionsDisabled;
  // Same lock as add/set-index (OR-4688).
  const isDragDisabled = !isEditable || actionsDisabled;
  const draggableDragIds = rows.filter((row) => !row.isIndexDate).map((row) => row.dragId);

  const renderCriteriaGrid = () => {
    const grid = (
      <Box
        data-testid="cohort-detail-criteria-list"
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: 2,
          minWidth: 0,
        }}
      >
        {rows.map((row) =>
          isDragDisabled || row.isIndexDate ? (
            <CriterionCard key={row.key} row={row} />
          ) : (
            <SortableCriteriaItem key={row.key} id={row.dragId}>
              {(dragHandleProps) => <CriterionCard row={row} dragHandleProps={dragHandleProps} />}
            </SortableCriteriaItem>
          )
        )}
      </Box>
    );

    return isDragDisabled ? (
      grid
    ) : (
      <SortableCriteriaList itemIds={draggableDragIds} onReorder={handleReorderCriteria}>
        {grid}
      </SortableCriteriaList>
    );
  };

  return (
    <>
      <Card
        data-testid="cohort-detail-criteria"
        variant="outlined"
        sx={{
          // Figma section card: 24px padding, 24px title-to-content gap.
          padding: 6,
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
          minWidth: 0,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            rowGap: 1,
          }}
        >
          {/* Count matches the cards rendered below — index date included. */}
          <Typography variant="h6" component="h3">
            {`Criteria (${rows.length})`}
          </Typography>
          {isEditable && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
              <Button
                data-testid="cohort-detail-criteria-set-index-date"
                variant="outlined"
                size="small"
                disabled={setIndexDateDisabled}
                onClick={() => setIsSetIndexDateOpen(true)}
                sx={{ textTransform: 'none' }}
              >
                {intl.formatMessage({
                  id: 'projects.analyticalVariables.library.setIndexDate.label',
                })}
              </Button>
              {/* M4 visual audit: outlined, not contained — the header's
                  Process button is the page's only primary action. */}
              <Button
                data-testid="cohort-detail-criteria-add-from-library"
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                disabled={addFromLibraryDisabled}
                onClick={() => setIsAddFromLibraryOpen(true)}
                sx={{ textTransform: 'none' }}
              >
                {intl.formatMessage({
                  id: 'projects.analyticalVariables.library.addFromLibrary.buttonLabel',
                })}
              </Button>
            </Box>
          )}
        </Box>
        {isLoading ? (
          <Box
            data-testid="cohort-detail-criteria-loading"
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
              gap: 2,
              minWidth: 0,
            }}
          >
            {[0, 1].map((i) => (
              <Skeleton key={i} variant="rectangular" height={140} sx={{ borderRadius: 1 }} />
            ))}
          </Box>
        ) : rows.length === 0 ? (
          <Typography
            variant="body2"
            color="text.secondary"
            data-testid="cohort-detail-criteria-empty"
            sx={{ textAlign: 'center', paddingY: 4 }}
          >
            No criteria defined yet. Use the Research Assistant — or the buttons above — to add
            analytical variables and set the index date.
          </Typography>
        ) : (
          renderCriteriaGrid()
        )}
      </Card>
      {isAddFromLibraryOpen && (
        <AddVariableFromLibraryModal onClose={() => setIsAddFromLibraryOpen(false)} />
      )}
      {isSetIndexDateOpen && <SetIndexDateModal onClose={() => setIsSetIndexDateOpen(false)} />}
    </>
  );
};
